/* RPLNSH theme script: marquee-text, accordion-disclosure and cart-count custom elements,
   plus section reveal, anchor glide and flavour intent. Native APIs only. */

/* <rp-marquee-text speed="0.1" direction="start-to-end" pause-on-hover>
   The light DOM keeps the first child plus N pairs of absolutely positioned clones
   at plus and minus 100% steps (N = host width over child width). The shadow slot
   is the scroller, translated one child width per loop; its transform makes it the
   containing block, so each 100% step is one child width. Speed is pixels per
   millisecond (default 0.12). Clones are aria-hidden. */
class MarqueeText extends HTMLElement {
  static get observedAttributes() { return ['speed', 'direction']; }

  constructor() {
    super();
    this.observer = new ResizeObserver(() => this.build());
    if (this.hasAttribute('pause-on-hover')) {
      this.addEventListener('pointerenter', () => this.animation?.pause());
      this.addEventListener('pointerleave', () => this.animation?.play());
    }
  }

  connectedCallback() {
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' }).innerHTML = '<style>:host{display:block}slot{display:flex;width:max-content;transform:translateX(0);will-change:transform}</style><slot part="scroller"></slot>';
    }
    const first = this.querySelector(':scope > :not([data-clone])');
    if (first && !this.item) this.item = first.cloneNode(true);
    this.observer.observe(this);
    requestAnimationFrame(() => this.build());
  }

  disconnectedCallback() { this.observer.disconnect(); this.animation?.cancel(); }
  attributeChangedCallback() { if (this.isConnected) this.build(); }

  build() {
    if (!this.item || !this.shadowRoot) return;
    const item = this.item.cloneNode(true);
    this.replaceChildren(item);
    const width = item.clientWidth;
    if (!width) return;
    const count = Math.ceil(this.clientWidth / width);
    const fragment = document.createDocumentFragment();
    for (let i = 1; i <= count; i++) {
      for (const sign of [-1, 1]) {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', '');
        clone.style.cssText = `position: absolute; inset-inline-start: calc(${100 * i * sign}%);`;
        fragment.appendChild(clone);
      }
    }
    this.appendChild(fragment);
    const scroller = this.shadowRoot.querySelector('[part="scroller"]');
    this.animation?.cancel();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const direction = this.getAttribute('direction') === 'end-to-start' ? -1 : 1;
    const speed = parseFloat(this.getAttribute('speed') || '0.12');
    this.animation = scroller.animate(
      [{ transform: 'translateX(0px)' }, { transform: `translateX(${direction * width}px)` }],
      { duration: width / speed, iterations: Infinity, easing: 'linear' }
    );
  }
}
if (!customElements.get('rp-marquee-text')) customElements.define('rp-marquee-text', MarqueeText);

/* <rp-accordion-disclosure> wraps a native <details> and keeps aria-expanded on the summary.
   The open height animates so content below eases instead of jumping in one frame;
   the panel content only fades and rises (opacity, transform). A second click
   mid-animation reverses from the current height. */
class AccordionDisclosure extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;
    this.summary = this.details.querySelector('summary');
    this.content = this.summary?.nextElementSibling;
    this.summary?.addEventListener('click', (e) => this.toggle(e));
    this.classList.toggle('rp-is-open', this.details.open);
    this.summary?.setAttribute('aria-expanded', String(this.details.open));
  }

  toggle(e) {
    e.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const opening = !this.classList.contains('rp-is-open');
    const from = this.details.getBoundingClientRect().height;
    this.anim?.cancel();
    this.fade?.cancel();
    this.classList.toggle('rp-is-open', opening);
    this.summary.setAttribute('aria-expanded', String(opening));
    const done = () => { this.details.style.overflow = ''; if (!opening) this.details.open = false; this.anim = null; this.fade?.cancel(); this.fade = null; };
    if (opening) this.details.open = true;
    if (reduce) return done();
    const to = opening ? this.details.scrollHeight : this.summary.getBoundingClientRect().height;
    this.details.style.overflow = 'hidden';
    this.anim = this.details.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 400, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' });
    this.anim.onfinish = done;
    if (this.content) this.fade = this.content.animate(opening ? [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }] : [{ opacity: 1 }, { opacity: 0 }], { duration: opening ? 280 : 160, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: 'both' });
  }
}
if (!customElements.get('rp-accordion-disclosure')) customElements.define('rp-accordion-disclosure', AccordionDisclosure);

/* Cart count badge, updated by cart:changed events. Adds rp-bump when the count rises (the header
   badge animates it), and re-reads the cart when the page comes back from the back/forward cache,
   where the count would otherwise be the one from before the visitor left. */
class CartCount extends HTMLElement {
  connectedCallback() {
    const show = (n) => {
      const s = this.querySelector('span');
      const before = s ? Number(s.textContent) || 0 : 0;
      this.hidden = n === 0;
      if (s) s.textContent = n;
      if (n > before) { this.classList.remove('rp-bump'); void this.offsetWidth; this.classList.add('rp-bump'); }
    };
    document.addEventListener('cart:changed', (e) => show(e.detail?.item_count ?? 0));
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => { if (cart) show(cart.item_count); })
        .catch(() => { /* keep the count it has */ });
    });
  }
}
if (!customElements.get('rp-cart-count')) customElements.define('rp-cart-count', CartCount);

/* ---- Motion. Transform and opacity only; nothing animates under reduced motion.
   1. Sections that start below the first screen fade up once as they arrive.
   2. Same-page anchor links scroll smoothly and land clear of the fixed header.
   The hero photograph stays still: its scroll drift was removed on 9 Oct because it jolted the page. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  const reveal = () => {
    if (reduce.matches || !('IntersectionObserver' in window)) return;
    const sections = [...document.querySelectorAll('main > .shopify-section')];
    const fold = window.innerHeight;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add('is-revealed');
        io.unobserve(e.target);
      }
    }, { rootMargin: '0px 0px -12% 0px' });
    for (const s of sections) {
      if (s.getBoundingClientRect().top < fold) continue;
      s.classList.add('rp-reveal');
      io.observe(s);
    }
  };

  const glide = () => {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href*="#"]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const url = new URL(a.href, location.href);
      if (url.pathname !== location.pathname || url.search !== location.search || !url.hash) return;
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'start' });
      history.pushState(null, '', url.hash);
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  };

  /* Flavour intent. Any element carrying data-rp-flavour="<handle>"
     is a flavour control: pointing at it or focusing it previews that flavour,
     clicking it picks it. A select marked data-rp-flavour-select picks on
     change. Leaving returns to the pick, or to the resting green. The root's
     data-rp-flavour drives --rp-cycle in rplnsh.css; sections listen for the
     rp:flavour event (detail.flavour, '' for none) to move their own pieces.
     window.RP.setFlavour(handle) and RP.previewFlavour(handle) do the same
     from code. Touch has no hover, so on a phone a tap is the intent. */
  const flavour = () => {
    const root = document.documentElement;
    const known = new Set(['orange-mango', 'watermelon', 'lychee', 'green-apple']);
    const handleize = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    let picked = '';
    /* force: a pick announces itself even when hover already shows that flavour */
    const show = (h, force = false) => {
      const next = known.has(h) ? h : '';
      if (!force && (root.dataset.rpFlavour || '') === next) return;
      if (next) root.dataset.rpFlavour = next; else delete root.dataset.rpFlavour;
      document.dispatchEvent(new CustomEvent('rp:flavour', { detail: { flavour: next, picked } }));
    };
    /* the root carries the same attribute, so it never counts as a control */
    const of = (el) => { const c = el instanceof Element ? el.closest('[data-rp-flavour]') : null; return c && c !== root ? c.dataset.rpFlavour : undefined; };
    document.addEventListener('pointerover', (e) => { if (e.pointerType !== 'touch') { const h = of(e.target); if (h) show(h); } });
    document.addEventListener('pointerout', (e) => { if (e.pointerType !== 'touch' && of(e.target) && !of(e.relatedTarget)) show(picked); });
    document.addEventListener('focusin', (e) => { const h = of(e.target); if (h) show(h); });
    document.addEventListener('focusout', (e) => { if (of(e.target) && !of(e.relatedTarget)) show(picked); });
    document.addEventListener('click', (e) => { const h = of(e.target); if (h) { picked = known.has(h) ? h : ''; show(h, true); } });
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (t instanceof HTMLSelectElement && t.hasAttribute('data-rp-flavour-select')) { const h = handleize(t.value); picked = known.has(h) ? h : ''; show(h, true); }
    });
    window.RP = Object.assign(window.RP || {}, {
      setFlavour: (h) => { picked = known.has(h) ? h : ''; show(picked, true); },
      previewFlavour: (h) => show(h || picked),
      flavour: () => root.dataset.rpFlavour || '',
    });
  };

  const boot = () => { reveal(); glide(); flavour(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/* Stamped reviews. Read from Stamped's public widget API, the same endpoint Stamped's own
   widget calls, with the store's public key (theme settings > Reviews). Returns every
   published review for one product, or for the whole store when no product is given,
   normalised to { id, rating, title, message, author, product }. One request chain per
   product per visit; the result is kept for the session. Fires rp:reviews-ready once. */
(() => {
  const memo = new Map();
  const read = (k) => { try { return JSON.parse(sessionStorage.getItem(k)); } catch { return null; } };
  const write = (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* storage full or blocked */ } };
  const list = ({ apiKey, storeUrl, productId, max = 200 }) => {
    if (!apiKey || !storeUrl) return Promise.reject(new Error('Stamped public key or store URL is not set in theme settings'));
    const id = `rp-stamped:${storeUrl}:${productId || 'all'}:${max}`;
    if (memo.has(id)) return memo.get(id);
    const run = (async () => {
      const cached = read(id);
      if (cached) return cached;
      const reviews = [];
      let page = 1;
      let total = Infinity;
      while (reviews.length < Math.min(max, total)) {
        const q = new URLSearchParams({ apiKey, storeUrl, take: '100', page: String(page) });
        if (productId) q.set('productId', String(productId));
        const res = await fetch(`https://stamped.io/api/widget/reviews?${q}`);
        if (!res.ok) throw new Error(`Stamped answered ${res.status}`);
        const body = await res.json();
        total = Number(body.total) || 0;
        const rows = body.data || [];
        if (!rows.length) break;
        for (const r of rows) {
          reviews.push({
            id: r.id,
            rating: Number(r.reviewRating) || 0,
            title: String(r.reviewTitle || '').trim(),
            message: String(r.reviewMessage || '').trim(),
            author: String(r.author || '').trim(),
            product: String(r.productName || '').trim(),
          });
        }
        page += 1;
      }
      const out = { total, reviews: reviews.slice(0, max) };
      write(id, out);
      return out;
    })();
    memo.set(id, run);
    run.catch((err) => { memo.delete(id); console.error('[rp-reviews] Stamped request failed:', String(err && err.message || err).slice(0, 200)); });
    return run;
  };
  /* text with line breaks kept, never parsed as HTML */
  const text = (el, value) => {
    el.textContent = '';
    String(value).split(/\r?\n/).forEach((line, i) => { if (i) el.append(document.createElement('br')); el.append(line); });
  };
  window.RP = Object.assign(window.RP || {}, { reviews: { list, text } });
  document.dispatchEvent(new CustomEvent('rp:reviews-ready'));
})();


/* RPCart, moved here from snippets/rp-product-card.liquid. Shopify only loads a snippet script on pages that
   render that snippet, so on pages without product cards the cart drawer upsells fell back to a full page post. */
/* RPCart: the one add path for every entry point in the theme.
     POST /cart/add.js, GET /cart.js, then cart:changed with
     { ...cart, source, added }, where added holds the cart lines the request
     touched. The cart drawer listens and is the only confirmation.
     RPCart.run also drives an add button's states. Snippet scripts load on
     every page, so the helper is there wherever a card, the chooser or the
     drawer is. */
  (() => {
    if (window.RPCart) return;
    const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const planOf = (line) => Number((line.selling_plan_allocation && line.selling_plan_allocation.selling_plan && line.selling_plan_allocation.selling_plan.id) || 0);
    const json = (r) => { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); };

    function match(cart, items) {
      const out = [];
      for (const it of items) {
        const line = (cart.items || []).find((l) => Number(l.variant_id) === Number(it.id) && planOf(l) === Number(it.selling_plan || 0));
        if (line && !out.includes(line)) out.push(line);
      }
      return out;
    }

    async function add(items, source, opener) {
      await fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items }),
      }).then(json);
      const cart = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then(json);
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: { ...cart, source, added: match(cart, items), opener } }));
      return cart;
    }

    const timers = new WeakMap();
    function state(btn, s) {
      btn.dataset.state = s;
      if (s === 'busy') btn.setAttribute('aria-disabled', 'true'); else btn.removeAttribute('aria-disabled');
    }

    async function run(btn, items, source) {
      if (btn.dataset.state === 'busy') return null;
      clearTimeout(timers.get(btn));
      state(btn, 'busy');
      let cart = null;
      try { cart = await add(items, source, btn); state(btn, 'done'); }
      catch (e) { state(btn, 'error'); }
      timers.set(btn, setTimeout(() => state(btn, 'idle'), 2400));
      return cart;
    }

    window.RPCart = { add, match, run, state };

    /* the card button. The card holds its hover look while the drawer is up,
       so nothing behind the overlay changes in the moment of the add */
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest ? e.target.closest('[data-rp-card-add]') : null;
      if (!btn) return;
      e.preventDefault();
      const card = btn.closest('.rp-fp__card');
      if (card) card.classList.add('is-held');
      const cart = await run(btn, [{ id: Number(btn.dataset.variantId), quantity: 1 }], 'card');
      if (!cart && card) card.classList.remove('is-held');
    });
    document.addEventListener('dialog:after-hide', () => {
      for (const c of document.querySelectorAll('.rp-fp__card.is-held')) c.classList.remove('is-held');
    });
  })();
